import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import type { ContactHostFormProps } from './ContactHostForm';
import { useDeliverGuestNotFoundMessageMutation } from './ContactHostForm.graphql.generated';

interface ContactHostFormFields
  extends Readonly<{
    name: string;
    email: string;
  }> {}

export function useContactHostFormController({ eventId, onClose }: ContactHostFormProps) {
  const { tq } = useTranslation('guestUnlock');
  const { toast } = useToast();
  const [deliverMessage, { loading }] = useDeliverGuestNotFoundMessageMutation({
    onCompleted: onClose,
  });

  const formik = useFormik<ContactHostFormFields>({
    initialValues: {
      name: '',
      email: '',
    },
    validationSchema: Yup.object<ContactHostFormFields>({
      name: Yup.string()
        .trim()
        .required(tq`All fields are required`),
      email: Yup.string()
        .trim()
        .required(tq`All fields are required`)
        .email(tq`Please enter a valid email`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ name, email }) => {
      try {
        await deliverMessage({
          variables: {
            payload: { eventId, guest: { firstName: name.trim(), email: email.trim() } },
          },
        });
      } catch (error) {
        addError(error, { context: 'UnlockDialog/ContactHostForm', operation: 'onSubmit', eventId });
        toast(tq`Something went wrong. Please try again.`);
      }
    },
  });

  const inputError = formik.errors.name || formik.errors.email || '';

  // Keyed on submitCount so a repeat submit with the same validation error
  // (e.g. tapping Send twice on an empty form) re-fires the toast.
  useEffect(() => {
    if (inputError) {
      toast(inputError);
    }
  }, [inputError, formik.submitCount, toast]);

  return {
    formik,
    loading,
  };
}
